import { makeStyles } from "@material-ui/core";
import { colorsLight } from "../../themes";

export interface EditorStyleProps {
  readonly: boolean;
  error: boolean;
  hasLabel: boolean;
  hasValue: boolean;
  hasFocus: boolean;
}

export const useEditorStyles = makeStyles((theme) => {
  const isDarkTheme = theme.palette.type === "dark";
  return {
    root: {
      transition: theme.transitions.create(["border-color"]),
      border: ({ readonly, error, hasFocus }: EditorStyleProps) =>
        readonly
          ? "none"
          : error
          ? `1px solid ${theme.palette.error.dark}`
          : hasFocus
          ? `1px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
      "&:hover": {
        border: ({ readonly, error }: EditorStyleProps) =>
          readonly
            ? "none"
            : error
            ? `1px solid ${theme.palette.error.dark}`
            : `1px solid ${theme.palette.primary.main}`,
      },
      borderRadius: "4px",
      display: "flex",
      flexDirection: "column-reverse",
      marginBottom: ({ readonly }: EditorStyleProps) => (readonly ? "auto" : theme.spacing(2)),
      backgroundColor: isDarkTheme ? theme.palette.background.default : "auto",
      position: "relative",
    },
    editorContainer: {
      position: "relative",
      paddingTop: ({ hasLabel }: EditorStyleProps) => (hasLabel ? theme.spacing(3) : 0),
    },
    editor: {
      fontFamily: theme.typography.fontFamily,
      minHeight: ({ readonly }: EditorStyleProps) => (readonly ? "auto" : 160),
      ...theme.typography.body1,
      padding: ({ readonly }: EditorStyleProps) => (readonly ? 0 : theme.spacing(0, 1.25)),
      outline: "none",
      "overflow-wrap": "anywhere !important",
      "& ::selection": {
        backgroundColor: "#B4D5FE",
      },
      "& p": {
        margin: 0,
        marginBottom: theme.spacing(1),
      },
      "& ul, & ol": {
        paddingLeft: theme.spacing(3),
        marginBottom: theme.spacing(1),
      },
      "& h1": {
        ...theme.typography.h1,
        margin: 0,
        marginBottom: theme.spacing(1),
      },
      "& h2": {
        ...theme.typography.h2,
        margin: 0,
        marginBottom: theme.spacing(1),
      },
      "& h3": {
        ...theme.typography.h3,
        margin: 0,
        marginBottom: theme.spacing(1),
      },
      "& .editor-text-bold": {
        fontWeight: "bold",
      },
      "& .editor-text-italic": {
        fontStyle: "italic",
      },
      "& .editor-text-underline": {
        textDecoration: "underline",
      },
      "& .editor-text-strikethrough": {
        textDecoration: "line-through",
      },
      "& .editor-text-underline.editor-text-strikethrough": {
        textDecoration: "underline line-through",
      },
      "& a": {
        color: theme.palette.primary.main,
        textDecoration: "underline",
        cursor: "text",
        "&:hover": {
          opacity: 0.8,
        },
      },
    },
    toolbar: {
      display: "flex",
      padding: theme.spacing(1),
      alignItems: "center",
      gap: theme.spacing(1),
      alignSelf: "stretch",
      borderRadius: "0 0 3px 3px",
      borderTop: "1px solid #DADFE8",
      background: "#FFF",
    },
    toolbarGroup: {
      display: "flex",
      gap: theme.spacing(0.5),
      "&:not(:last-child)": {
        borderRight: `1px solid ${theme.palette.divider}`,
        paddingRight: theme.spacing(1),
        marginRight: theme.spacing(1),
      },
    },
    toolbarButton: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
      borderRadius: "4px",
      padding: theme.spacing(0.5),
      background: theme.palette.background.paper,
      cursor: "pointer",
      opacity: 0.6,
      width: 32,
      height: 32,
      "&:hover:not(:disabled)": {
        opacity: 1,
        background: "#F5F6F7",
      },
      "&:disabled": {
        cursor: "not-allowed",
        opacity: 0.3,
        background: "#FFF",
      },
    },
    toolbarButtonActive: {
      background: "#F0F2F5",
      opacity: 1,
      "&:hover:not(:disabled)": {
        background: "#DADFE8",
      },
    },
    label: {
      color: ({ error }: EditorStyleProps) => (error ? theme.palette.error.dark : colorsLight.black.mediumEmphasis),
      position: "absolute",
      width: "90%",
      transform: ({ hasValue, hasFocus }: EditorStyleProps) =>
        hasValue || hasFocus ? "translate(12px, 10px) scale(0.75)" : "translate(12px, 20px) scale(1)",
      transformOrigin: "top left",
      transition: theme.transitions.create(["transform"]),
      zIndex: 1,
      pointerEvents: "none",
    },
    errorCaption: {
      color: theme.palette.error.dark,
    },
  };
});

export const useFloatingLinkEditorStyles = makeStyles((theme) => ({
  container: {
    position: "fixed",
    zIndex: 10000,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
    padding: theme.spacing(0.75, 1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    "&::before": {
      content: '""',
      position: "absolute",
      left: "var(--arrow-left, 50%)",
      width: 12,
      height: 12,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
    },
    '&[data-placement="above"]::before': {
      bottom: -8,
      transform: "translateX(-50%) rotate(45deg)",
      borderTop: "none",
      borderLeft: "none",
    },
    '&[data-placement="below"]::before': {
      top: -8,
      transform: "translateX(-50%) rotate(45deg)",
      borderBottom: "none",
      borderRight: "none",
    },
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.75rem",
    marginTop: -2,
    marginLeft: 2,
  },
  linkInput: {
    flex: 1,
    fontSize: "0.875rem",
    padding: "6px 8px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    outline: "none",
    resize: "none",
    lineHeight: 1.4,
    overflow: "hidden",
    wordBreak: "break-all" as const,
    fontFamily: "inherit",
    "&:focus": {
      borderColor: theme.palette.primary.main,
    },
  },
  linkInputError: {
    borderColor: theme.palette.error.main,
  },
}));

export const useFloatingLinkEditorPluginStyles = makeStyles((theme) => ({
  linkEditor: {
    position: "absolute",
    width: "auto",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    "&::after": {
      content: '""',
      position: "absolute",
      left: "var(--arrow-left, 50%)",
      width: 10,
      height: 10,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
    },
    '&[data-placement="above"]::after': {
      bottom: -6,
      transform: "translateX(-50%) rotate(45deg)",
      borderTop: "none",
      borderLeft: "none",
    },
    '&[data-placement="below"]::after': {
      top: -6,
      transform: "translateX(-50%) rotate(45deg)",
      borderBottom: "none",
      borderRight: "none",
    },
  },
  linkInput: {
    flex: 1,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    padding: theme.spacing(0.75, 1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    outline: "none",
    resize: "none",
    lineHeight: 1.4,
    overflow: "hidden",
    wordBreak: "break-all",
    "&:focus": {
      borderColor: theme.palette.primary.main,
    },
  },
  linkText: {
    flex: 1,
    fontSize: "14px",
    overflow: "hidden",
    wordBreak: "break-all",
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));
